import { motion, useReducedMotion } from 'framer-motion'
import { stagger, wordSwing, VIEWPORT } from '../../utils/motion'

// Display type that arrives word by word: each word lifts up out of its own
// mask, so the line seems to rise from behind a ledge. `text` may be a string
// or an array mixing strings and React nodes (a node counts as one word, so
// an accented span can be dropped in).
export default function KineticText({
  as = 'h2',
  text,
  className = '',
  each = 0.05,
  delay = 0,
  immediate = false,
  ...props
}) {
  const reduce = useReducedMotion()
  const Tag = motion[as] || motion.h2
  const parts = (Array.isArray(text) ? text : [text]).flatMap((t) =>
    typeof t === 'string' ? t.split(' ').filter(Boolean) : [t]
  )
  const label = parts.map((p) => (typeof p === 'string' ? p : p?.props?.children ?? '')).join(' ')

  if (reduce) {
    return (
      <Tag
        initial={{ opacity: 0 }}
        {...(immediate ? { animate: { opacity: 1 } } : { whileInView: { opacity: 1 }, viewport: VIEWPORT })}
        transition={{ duration: 0.3, delay }}
        className={className}
        {...props}
      >
        {parts.map((p, i) => (
          <span key={i}>{p}{i < parts.length - 1 ? ' ' : ''}</span>
        ))}
      </Tag>
    )
  }

  return (
    <Tag
      variants={stagger(each, delay)}
      initial="hidden"
      {...(immediate ? { animate: 'show' } : { whileInView: 'show', viewport: VIEWPORT })}
      className={className}
      aria-label={label}
      {...props}
    >
      {parts.map((p, i) => (
        // The mask: clips the word while it is below its baseline. The padding
        // and matching negative margin leave room for descenders.
        <span key={i} className="inline-block overflow-hidden whitespace-pre pb-[0.14em] -mb-[0.14em] align-baseline">
          <motion.span variants={wordSwing} className="inline-block will-change-transform" aria-hidden="true">
            {p}
          </motion.span>
          {i < parts.length - 1 ? ' ' : ''}
        </span>
      ))}
    </Tag>
  )
}
